import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, Transition } from "@headlessui/react";
import { motion, AnimatePresence } from "framer-motion";
import { useUser } from "../contexts/UserContext";
import logo_dark from "../assets/images/logo-icon.png";
import { signOut } from "../domain/services/auth/authService";
import { Avatar } from "flowbite-react";
import {
  HiOutlineLogout,
  HiOutlineMenu,
  HiX,
  HiOutlineHome,
  HiOutlineCreditCard,
  HiOutlineUserCircle,
  HiOutlineChatAlt,
  HiOutlineQuestionMarkCircle,
} from "react-icons/hi";
import { Breadcrumb } from "./BreadCrumb";

interface NavItem {
  path?: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  description?: string;
  submenu?: SubNavItem[];
}

interface SubNavItem {
  path: string;
  label: string;
  description?: string;
}

const navLinks: NavItem[] = [
  {
    path: "/dashboard",
    label: "Dashboard",
    icon: HiOutlineHome,
    description: "Overview of your finances",
  },
  {
    label: "Transactions",
    icon: HiOutlineCreditCard,
    description: "Manage your transactions",
    submenu: [
      {
        path: "/transactions/all",
        label: "All Transactions",
        description: "View and manage all transactions",
      },
      {
        path: "/transactions/recurring",
        label: "Recurring Transactions",
        description: "Manage recurring payments",
      },
    ],
  },
  {
    label: "Profile",
    icon: HiOutlineUserCircle,
    description: "Manage your account",
    submenu: [
      {
        path: "/profile/accounts",
        label: "My Profile",
        description: "Update your personal information",
      },
      {
        path: "/profile/banks",
        label: "Bank Accounts",
        description: "Manage connected bank accounts",
      },
      {
        path: "/profile/family",
        label: "Family",
        description: "Manage family members",
      },
    ],
  },
  {
    path: "/chat",
    label: "Chat",
    icon: HiOutlineChatAlt,
    description: "Chat with AI assistant",
  },
  {
    path: "/faq",
    label: "FAQ",
    icon: HiOutlineQuestionMarkCircle,
    description: "Get help and support",
  },
];

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { currentUser, providerUser } = useUser();
  const location = useLocation();
  const [dragConstraints, setDragConstraints] = useState({ top: 0, bottom: 0 });
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  // Page transition variants
  const pageTransition = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
  };

  // Mobile menu animation variants
  const mobileMenuVariants = {
    closed: {
      opacity: 0,
      y: "-100%",
      boxShadow: "0 0 0 rgba(0, 0, 0, 0)",
    },
    open: {
      opacity: 1,
      y: 0,
      boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
    },
  };
  useEffect(() => {
    if (mobileMenuRef.current) {
      setDragConstraints({
        top: -mobileMenuRef.current.offsetHeight,
        bottom: 0,
      });
    }
  }, [mobileMenuRef]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollThreshold = 20;
      setIsScrolled(window.scrollY > scrollThreshold);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const NavLink = ({
    item,
    isMobile = false,
  }: {
    item: any;
    isMobile?: boolean;
  }) => {
    const isActive = location.pathname === item.path;
    const baseClasses = `
      group flex items-center space-x-2 rounded-lg transition-all duration-200
      ${isMobile ? "p-3" : "px-4 py-2"}
      ${
        isActive
          ? "bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400"
          : "hover:bg-gray-50 dark:hover:bg-gray-800"
      }
    `;

    return (
      <Link to={item.path} className={baseClasses}>
        {item.icon && (
          <item.icon
            className={`w-5 h-5 ${isActive ? "text-indigo-600 dark:text-indigo-400" : "text-gray-500 dark:text-gray-400"}`}
          />
        )}
        <div className="flex flex-col">
          <span className={`${isActive ? "font-semibold" : ""}`}>
            {item.label}
          </span>
          {isMobile && item.description && (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {item.description}
            </span>
          )}
        </div>
      </Link>
    );
  };

  const DropdownMenu = ({
    item,
    isMobile = false,
  }: {
    item: any;
    isMobile?: boolean;
  }) => {
    return (
      <Menu as="div" className="relative">
        {({ open, close }: { open: boolean; close: () => void }) => (
          <>
            <Menu.Button
              className={`
              group flex items-center space-x-2 rounded-lg transition-all duration-200 w-full
              ${isMobile ? "p-3" : "px-4 py-2"}
              ${
                open
                  ? "bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400"
                  : "hover:bg-gray-50 dark:hover:bg-gray-800"
              }
            `}
            >
              {item.icon && (
                <item.icon
                  className={`w-5 h-5 ${open ? "text-indigo-600 dark:text-indigo-400" : "text-gray-500 dark:text-gray-400"}`}
                />
              )}
              <div className="flex flex-col flex-grow">
                <div className="flex items-center justify-between">
                  <span className={open ? "font-semibold" : ""}>
                    {item.label}
                  </span>
                  <motion.svg
                    className="w-4 h-4 ml-2"
                    animate={{ rotate: open ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </motion.svg>
                </div>
                {isMobile && item.description && (
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {item.description}
                  </span>
                )}
              </div>
            </Menu.Button>

            <Transition
              enter="transition duration-100 ease-out"
              enterFrom="transform scale-95 opacity-0"
              enterTo="transform scale-100 opacity-100"
              leave="transition duration-75 ease-in"
              leaveFrom="transform scale-100 opacity-100"
              leaveTo="transform scale-95 opacity-0"
            >
              <Menu.Items
                className={`
                absolute z-10 mt-2 rounded-lg bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none
                ${isMobile ? "w-full" : "w-56 right-0"}
              `}
              >
                <div className="p-1">
                  {item.submenu.map((subItem: any, idx: number) => (
                    <Menu.Item key={idx}>
                      {({ active }: { active: boolean }) => (
                        <Link
                          to={subItem.path}
                          className={`
                            flex flex-col rounded-lg px-4 py-2
                            ${active ? "bg-indigo-50 dark:bg-indigo-900/20" : ""}
                          `}
                          onClick={() => {
                            if (isMobile) {
                              setIsMobileMenuOpen(false);
                            }
                            close();
                          }}
                        >
                          <span
                            className={
                              location.pathname === subItem.path
                                ? "font-semibold text-indigo-600 dark:text-indigo-400"
                                : ""
                            }
                          >
                            {subItem.label}
                          </span>
                          {subItem.description && (
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              {subItem.description}
                            </span>
                          )}
                        </Link>
                      )}
                    </Menu.Item>
                  ))}
                </div>
              </Menu.Items>
            </Transition>
          </>
        )}
      </Menu>
    );
  };

  return (
    <nav
      className={`
        fixed w-full z-50 transition-all duration-300
        ${
          isScrolled
            ? "bg-white/90 dark:bg-slate-900/90 backdrop-blur-lg shadow-lg"
            : "bg-transparent"
        }
      `}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <motion.img
              src={logo_dark}
              alt="OmniButler"
              className="h-10 w-10"
              whileHover={{ scale: 1.1 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            />
            <span className="text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent transition-colors duration-300">
              OmniButler
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex md:items-center md:space-x-1">
            {currentUser ? (
              <>
                {navLinks.map((item, index) => (
                  <React.Fragment key={index}>
                    {item.submenu ? (
                      <DropdownMenu item={item} />
                    ) : (
                      <NavLink item={item} />
                    )}
                  </React.Fragment>
                ))}

                {/* User Menu */}
                <Menu as="div" className="relative ml-4">
                  <Menu.Button className="flex items-center space-x-2 rounded-lg p-2 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200">
                    <Avatar
                      img={providerUser?.photoURL || undefined}
                      rounded
                      size="sm"
                      className="ring-2 ring-indigo-500 ring-offset-2 dark:ring-offset-gray-800"
                    />
                    <span className="hidden lg:block font-medium">
                      {providerUser?.displayName || "User"}
                    </span>
                  </Menu.Button>

                  <Transition
                    enter="transition duration-100 ease-out"
                    enterFrom="transform scale-95 opacity-0"
                    enterTo="transform scale-100 opacity-100"
                    leave="transition duration-75 ease-in"
                    leaveFrom="transform scale-100 opacity-100"
                    leaveTo="transform scale-95 opacity-0"
                  >
                    <Menu.Items className="absolute right-0 mt-2 w-48 rounded-lg bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      <div className="p-1">
                        <Menu.Item>
                          {({ active }: { active: boolean }) => (
                            <button
                              onClick={() => signOut()}
                              className={`
                                      flex w-full items-center space-x-2 rounded-lg px-4 py-2
                                      ${active ? "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400" : ""}
                                    `}
                            >
                              <HiOutlineLogout className="w-5 h-5" />
                              <span>Sign Out</span>
                            </button>
                          )}
                        </Menu.Item>
                      </div>
                    </Menu.Items>
                  </Transition>
                </Menu>
              </>
            ) : (
              <div className="flex items-center space-x-4">
                <Link
                  to="/login"
                  className="px-4 py-2 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200"
                >
                  Log In
                </Link>
                <Link
                  to="/signup"
                  className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors duration-200"
                >
                  Sign Up
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200"
          >
            {isMobileMenuOpen ? (
              <HiX className="w-6 h-6" />
            ) : (
              <HiOutlineMenu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            ref={mobileMenuRef}
            initial={pageTransition.initial}
            animate={pageTransition.animate}
            exit={pageTransition.exit}
            variants={mobileMenuVariants}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            drag="y"
            dragConstraints={dragConstraints}
            dragElastic={0.2}
            onDragEnd={(
              _event: MouseEvent | TouchEvent | PointerEvent,
              dragInfo: {
                offset: { y: number };
              },
            ) => {
              if (dragInfo.offset.y > 100) {
                setIsMobileMenuOpen(false);
              }
            }}
            className="fixed top-[80px] left-0 right-0 z-40 overflow-hidden bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-800 border-t dark:border-gray-800 backdrop-blur-lg"
          >
            {/* Breadcrumb Navigation */}
            <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-800">
              <Breadcrumb />
            </div>

            {/* Menu Items */}
            <motion.div
              className="container mx-auto px-4 py-4 space-y-2 max-h-[70vh] overflow-y-auto"
              variants={{
                open: {
                  transition: {
                    staggerChildren: 0.07,
                    delayChildren: 0.2,
                  },
                },
              }}
            >
              {navLinks.map((item, index) => (
                <motion.div
                  key={index}
                  variants={{
                    closed: { opacity: 0, x: -20 },
                    open: { opacity: 1, x: 0 },
                  }}
                >
                  {item.submenu ? (
                    <DropdownMenu item={item} isMobile />
                  ) : (
                    <NavLink item={item} isMobile />
                  )}
                </motion.div>
              ))}
            </motion.div>

            {/* Pull to close indicator */}
            <div className="text-center py-2 text-sm text-gray-500 dark:text-gray-400">
              Pull down to close
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
